import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { LogOut, Mail, Phone, User, Calendar, Image as ImageIcon } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  images: string[] | null;
  status: string;
  created_at: string;
}

const Admin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyEmail, setReplyEmail] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    checkAuth();
    fetchMessages();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('contact_messages_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contact_messages'
        },
        () => {
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/login");
      return;
    }
    
    // Verify admin role
    const { data: roles, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', session.user.id)
      .eq('role', 'admin')
      .single();
      
    if (error || !roles) {
      toast({
        title: "Accesso negato",
        description: "Non hai i permessi per accedere a questa pagina",
        variant: "destructive"
      });
      navigate("/");
    }
  };

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMessages(data || []);
      
      // Generate signed URLs for all images
      if (data) {
        const urlMap: Record<string, string> = {};
        for (const message of data) {
          if (message.images && Array.isArray(message.images)) {
            for (const imagePath of message.images) {
              const { data: signedUrlData } = await supabase.storage
                .from('contact-images')
                .createSignedUrl(imagePath, 3600); // 1 hour expiry
              
              if (signedUrlData?.signedUrl) {
                urlMap[imagePath] = signedUrlData.signedUrl;
              }
            }
          }
        }
        setImageUrls(urlMap);
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile caricare i messaggi",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('contact_messages')
        .update({ status: 'read' })
        .eq('id', id);

      if (error) throw error;
      fetchMessages();
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile aggiornare lo stato",
        variant: "destructive"
      });
    }
  };

  const handleReply = (message: ContactMessage) => {
    setReplyingTo(message.id);
    setReplyEmail(message.email);
    setReplyMessage(`Caro/a ${message.name},\n\nGrazie per averci contattato.\n\n`);
  };

  const sendReply = async () => {
    if (!replyingTo) return;

    try {
      // Update message status to replied
      const { error: updateError } = await supabase
        .from('contact_messages')
        .update({ status: 'replied' })
        .eq('id', replyingTo);

      if (updateError) throw updateError;

      // Create mailto link
      const subject = encodeURIComponent("Risposta alla tua richiesta - Mangimi Rubini");
      const body = encodeURIComponent(replyMessage);
      window.location.href = `mailto:${replyEmail}?subject=${subject}&body=${body}`;

      toast({
        title: "Risposta preparata",
        description: "Si aprirà il tuo client email per inviare la risposta"
      });

      setReplyingTo(null);
      setReplyEmail("");
      setReplyMessage("");
      fetchMessages();
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile inviare la risposta",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge variant="destructive">Nuovo</Badge>;
      case 'read':
        return <Badge variant="secondary">Letto</Badge>;
      case 'replied':
        return <Badge className="bg-green-500">Risposto</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Caricamento...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-primary" style={{ fontFamily: 'Verdana, sans-serif' }}>
            Dashboard Messaggi
          </h1>
          <Button onClick={handleLogout} variant="outline">
            <LogOut className="mr-2 h-4 w-4" />
            Esci
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid gap-6">
          {messages.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Nessun messaggio ricevuto
              </CardContent>
            </Card>
          ) : (
            messages.map((message) => (
              <Card key={message.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <CardTitle className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {message.name}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {message.email}
                        </span>
                        {message.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {message.phone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(message.created_at).toLocaleString('it-IT')}
                        </span>
                      </CardDescription>
                    </div>
                    {getStatusBadge(message.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium mb-2">Messaggio:</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {message.message}
                    </p>
                  </div>

                  {message.images && message.images.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-2 flex items-center gap-2">
                        <ImageIcon className="h-4 w-4" />
                        Immagini allegate:
                      </p>
                      <div className="grid grid-cols-3 gap-4">
                        {message.images.map((img, idx) => (
                          imageUrls[img] ? (
                            <a
                              key={idx}
                              href={imageUrls[img]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <img
                                src={imageUrls[img]}
                                alt={`Allegato ${idx + 1}`}
                                className="w-full h-32 object-cover rounded-lg hover:opacity-80 transition-opacity"
                              />
                            </a>
                          ) : (
                            <div key={idx} className="w-full h-32 bg-muted rounded-lg flex items-center justify-center">
                              <p className="text-sm text-muted-foreground">Caricamento...</p>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {replyingTo === message.id ? (
                    <div className="space-y-4 pt-4 border-t">
                      <div className="space-y-2">
                        <Label>Rispondi a {message.email}</Label>
                        <Textarea
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          rows={6}
                          placeholder="Scrivi la tua risposta..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={sendReply}>
                          Invia risposta
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyEmail("");
                            setReplyMessage("");
                          }}
                        >
                          Annulla
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      {message.status === 'new' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => markAsRead(message.id)}
                        >
                          Segna come letto
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => handleReply(message)}
                      >
                        Rispondi
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
